'use client'

import * as DialogPrimitive from '@radix-ui/react-dialog'
import Link from 'next/link'
import { useState } from 'react'

import { MobileActionSlot } from '@layouts/mobile-action'
import { cn } from '@lib/cn'
import { useAppRoute } from '@lib/use-app-route'
import { useOrganizations } from '@resolvers/organizations'

import EnvironmentMenu from './environment-menu'
import { EnvironmentSwitch } from './index'
import TeamMenu from './team-menu'
import { isNavActive, useSidebarNav, type SidebarItem } from './use-sidebar-nav'

const DrawerLink = ({ item, active, count, onNavigate }: { item: SidebarItem; active: boolean; count?: number; onNavigate: () => void }) => (
  <Link
    href={item.children?.[0]?.href ?? item.href}
    onClick={onNavigate}
    aria-current={active ? 'page' : undefined}
    className={cn(
      'flex items-center justify-between rounded-xl px-3.5 py-[13px] text-[17px] leading-[22px] outline-hidden',
      active ? 'bg-o-nav-active font-medium text-o-ink shadow-o-nav' : 'text-o-slate'
    )}
  >
    {item.label}
    {count !== undefined && <span className="text-[14px] font-normal leading-[18px] text-o-faint">{count}</span>}
  </Link>
)

const MenuDrawer = ({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) => {
  const { mainItems, currentPath } = useSidebarNav()
  const { scope, envPath } = useAppRoute()
  const { organizations } = useOrganizations(scope)

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed inset-0 z-50 flex flex-col overflow-y-auto bg-o-sidebar px-4 pb-[34px] pt-3 outline-hidden md:hidden"
        >
          <DialogPrimitive.Title className="sr-only">Menu</DialogPrimitive.Title>
          <div className="flex flex-col gap-5">
            <div className="flex items-center justify-between gap-3">
              <TeamMenu size="lg" />
              <DialogPrimitive.Close
                aria-label="Close menu"
                className="flex size-[38px] shrink-0 items-center justify-center rounded-full border border-o-line-strong bg-o-card text-o-ink outline-hidden"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                  <path d="M3.5 3.5l7 7M10.5 3.5l-7 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </DialogPrimitive.Close>
            </div>
            <EnvironmentSwitch size="lg" />
            <nav className="flex flex-col gap-0.5" aria-label="Main">
              {mainItems.map((item) => (
                <DrawerLink
                  key={item.label}
                  item={item}
                  active={isNavActive(currentPath, item.href, item.exact)}
                  count={item.href === envPath('/organizations') && organizations.length > 0 ? organizations.length : undefined}
                  onNavigate={() => onOpenChange(false)}
                />
              ))}
            </nav>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

const MobileNav = () => {
  const { team, scope, pathname } = useAppRoute()
  const { tenant } = useSidebarNav()
  const { organizations } = useOrganizations(scope)
  const [openedAt, setOpenedAt] = useState<string | null>(null)
  const open = openedAt === pathname
  const organization = tenant ? organizations.find((candidate) => candidate.tenant === tenant) : undefined
  const title = tenant ? (organization?.label ?? tenant) : (team?.name ?? 'Team')

  return (
    <header className="flex items-center justify-between gap-3 bg-o-panel px-4 pb-2.5 pt-3 md:hidden">
      <div className="flex min-w-0 items-center gap-3">
        <button
          type="button"
          aria-label="Open menu"
          aria-expanded={open}
          onClick={() => setOpenedAt(pathname)}
          className="flex size-[38px] shrink-0 items-center justify-center rounded-xl border border-o-line-strong bg-o-card text-o-ink outline-hidden"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
            <path d="M3 5h12M3 9h12M3 13h12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
        <span className="truncate text-[17px] font-semibold leading-[22px] tracking-[-0.01em] text-o-ink">{title}</span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <EnvironmentMenu variant="chip" />
        <MobileActionSlot />
      </div>
      <MenuDrawer open={open} onOpenChange={(next) => setOpenedAt(next ? pathname : null)} />
    </header>
  )
}

export default MobileNav
